import React, { forwardRef } from 'react';
import { Icon } from '../../design/Icon';

export const Chip = forwardRef(function Chip(
  { icon, keyLabel, active = false, onRemove, removeLabel = 'Remove', className = '', children, ...rest },
  ref
) {
  return (
    <button ref={ref} type="button" className={['chip', active ? 'active' : '', className].filter(Boolean).join(' ')} {...rest}>
      {icon && <Icon name={icon} />}
      {keyLabel && <span className="k">{keyLabel}</span>}
      {children}
      {onRemove && (
        <span
          className="x"
          role="button"
          tabIndex={0}
          aria-label={removeLabel}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }
          }}
        >
          <Icon name="x" className="ico-sm" />
        </span>
      )}
    </button>
  );
});

const TONE_CLASS = { neutral: '', good: 'b-good', warning: 'b-warning', serious: 'b-serious', critical: 'b-critical', info: 'b-info' };

export function Badge({ tone = 'neutral', icon, className = '', children }) {
  return (
    <span className={['badge', TONE_CLASS[tone], className].filter(Boolean).join(' ')}>
      {icon && <Icon name={icon} />}
      {children}
    </span>
  );
}

export const SYNTHETIC_DATA_LABEL = 'Includes synthetic demo data';

/** Shown wherever content from the synthetic (demo data) providers is in scope (G3 review). */
export function SyntheticDataBadge() {
  return (
    <span className="badge b-warning" data-testid="synthetic-data-label" title="Generated by the demo transcription and analysis providers, not from real calls">
      {SYNTHETIC_DATA_LABEL}
    </span>
  );
}

export function Dot({ color, className = '' }) {
  return <span className={`dot ${className}`.trim()} style={{ background: color }} aria-hidden="true" />;
}

// Encodings from docs/design/mockups/_build/shell-data.js (sevClass, sevIcon, clsTag). Keys accept the API enum names.
const SEVERITY = {
  Immediate: { label: 'Immediate', className: 'sev-immediate', icon: 'octagon', bar: 'var(--critical-mark)' },
  Within48Hours: { label: 'Within 48 Hours', className: 'sev-48h', icon: 'alert', bar: 'var(--serious-mark)' },
  Standard: { label: 'Standard', className: 'sev-standard', icon: 'info', bar: 'var(--border-strong)' },
  Informational: { label: 'Informational', className: 'sev-info', icon: 'info', bar: 'var(--divider)' },
};
SEVERITY['Within 48 Hours'] = SEVERITY.Within48Hours;

export const severityInfo = (severity) => SEVERITY[severity] || SEVERITY.Informational;

export function ClassificationTag({ category, subcategory, severity, full = false, className = '' }) {
  const sev = severityInfo(severity);
  const name = full && subcategory ? `${category} › ${subcategory}` : category;
  const title = `${category}${subcategory ? ` › ${subcategory}` : ''} · ${sev.label}`;
  return (
    <span className={`tag ${className}`.trim()} title={title}>
      <span className="sev-bar" style={{ background: sev.bar }} aria-hidden="true" />
      {name}
    </span>
  );
}

export function SeverityLabel({ severity }) {
  const sev = severityInfo(severity);
  return (
    <span className={`sev ${sev.className}`}>
      <Icon name={sev.icon} />
      {sev.label}
    </span>
  );
}

export function LanguagePill({ code, title }) {
  return (
    <span className="lang" title={title}>
      {String(code || '').toUpperCase()}
    </span>
  );
}

const SENTIMENT = {
  Positive: ['smile', 'info'],
  Neutral: ['meh', 'neutral'],
  Mixed: ['meh', 'warning'],
  Negative: ['frown', 'serious'],
};

export function SentimentBadge({ sentiment }) {
  if (!sentiment || !SENTIMENT[sentiment]) return <span className="muted small">—</span>;
  const [icon, tone] = SENTIMENT[sentiment];
  return (
    <Badge tone={tone} icon={icon}>
      {sentiment}
    </Badge>
  );
}

const OUTCOME = {
  Answered: ['good', 'check'],
  Transferred: ['info', 'forward'],
  Voicemail: ['neutral', 'voicemail'],
  Abandoned: ['critical', 'x'],
  Forwarded: ['info', 'forward'],
};

export function OutcomeBadge({ outcome }) {
  const [tone, icon] = OUTCOME[outcome] || ['neutral', 'info'];
  return (
    <Badge tone={tone} icon={icon}>
      {outcome}
    </Badge>
  );
}
